import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '~/services/logger';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import { isEmptyArray } from '~/services/layout/layout';

const COPY_FEEDBACK_DURATION_MS: number = 2000;

function getEntryCode(entry: FeedEntry): string {
  const code: string | undefined = entry?.extensions?.fyc_code;

  if (typeof code !== 'string') return '';

  return code.trim();
}

export function CustomCopyToClipboard({ feed }: { feed?: Feed }): JSX.Element {
  const [copiedEntryId, setCopiedEntryId] = useState<string | null>(null);
  const resetCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const feedEntries: FeedEntry[] = feed?.entry || [];

  useEffect(() => {
    return () => {
      if (!resetCopiedTimeoutRef.current) return;

      clearTimeout(resetCopiedTimeoutRef.current);
    };
  }, []);

  const handleCopyToClipboard = useCallback(
    async ({ code, entryId }: { code: string; entryId: string }) => {
      if (!code) return;
      if (typeof navigator === 'undefined' || !navigator.clipboard) return;

      try {
        await navigator.clipboard.writeText(code);
        setCopiedEntryId(entryId);

        if (resetCopiedTimeoutRef.current) {
          clearTimeout(resetCopiedTimeoutRef.current);
        }

        resetCopiedTimeoutRef.current = setTimeout(() => {
          setCopiedEntryId((currentCopiedEntryId: string | null) => {
            if (currentCopiedEntryId !== entryId) {
              return currentCopiedEntryId;
            }

            return null;
          });
        }, COPY_FEEDBACK_DURATION_MS);
      } catch (error: unknown) {
        const errorMessage: string =
          error instanceof Error ? error.message : 'Unknown error';

        logger.error(
          `CustomCopyToClipboard handleCopyToClipboard: ${errorMessage}`
        );
      }
    },
    []
  );

  if (isEmptyArray(feedEntries)) return <></>;

  const entriesWithCodes: Array<{ entryId: string; code: string }> = feedEntries
    .map((entry: FeedEntry) => ({
      entryId: entry.id,
      code: getEntryCode(entry),
    }))
    .filter((entry: { entryId: string; code: string }) => !!entry.code);

  if (isEmptyArray(entriesWithCodes)) return <></>;

  return (
    <section className="text-center">
      <div className="space-y-2">
        {entriesWithCodes.map(({ entryId, code }) => {
          const isCopied: boolean = copiedEntryId === entryId;

          return (
            <div
              key={entryId}
              className="flex items-center justify-center gap-4 py-2"
            >
              <div className="select-all text-3xl font-bold tracking-widest text-white">
                {code}
              </div>

              <div className="relative flex items-center">
                <button
                  type="button"
                  aria-label="Copy to clipboard"
                  onClick={() => {
                    void handleCopyToClipboard({ code, entryId });
                  }}
                  className="group relative flex h-11 w-11 items-center justify-center rounded-md border border-gray-700 transition-all duration-300 ease-in-out hover:border-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                >
                  <img
                    src="/copytoclip.png"
                    alt=""
                    aria-hidden
                    className={`absolute h-8 w-8 object-cover transition-all duration-300 ease-in-out [clip-path:inset(11%)] ${
                      isCopied ? 'scale-90 opacity-0' : 'scale-100 opacity-100'
                    }`}
                  />

                  <img
                    src="/copied.png"
                    alt=""
                    aria-hidden
                    className={`absolute h-11 w-11 object-contain transition-all duration-300 ease-in-out ${
                      isCopied ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
                    }`}
                  />
                </button>

                <span
                  className={`pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded bg-white px-2 py-1 text-xs font-bold text-black transition-opacity duration-300 ${
                    isCopied ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  Copied!
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
